// franklycatering #1557 — the per-event facts the Events board and List show:
// stage copy, the primary quote, money owing, service, flags and date text.
// No React and no queries, so every rule is unit-tested.

import { parseISO } from "date-fns";
import type { EventType } from "@/hooks/useEvents";
import type { ContactLastMessage, EventsPipelineData, PipelineEvent, PipelineQuote } from "@/hooks/useEventsPipeline";
import {
  CLOSED_STAGES,
  EVENT_STAGE_LABELS,
  committedQuotes,
  isLiveQuote,
  normalizeStage,
  quoteVersions,
  round2,
  type EventStage,
  type StageQuote,
} from "@/lib/event-pipeline";
import { QUOTE_STATUSES, SERVICE_TYPES, SERVICE_TYPE_SHORT_LABELS, type ServiceType } from "@/types/quotes";

export interface StageInfo {
  label: string;
  /** A CSS colour built from the theme tokens (index.css `--ev-*`). */
  color: string;
  /** "An event is here when". */
  in: string;
  /** "It moves on when". */
  out: string;
  /** What changed in #1557, shown in the accent box. */
  chg?: string;
  /** Post-event gets a "New" badge. */
  isNew?: boolean;
}

const CLOSED_OUT = "Closed. Pick it in Event status to see it.";

/** The ⓘ copy on each column, word for word from the approved prototype. */
export const STAGE_INFO: Record<EventStage, StageInfo> = {
  lead: {
    label: EVENT_STAGE_LABELS.lead,
    color: "hsl(var(--muted-foreground))",
    in: "A new enquiry with no quote yet.",
    out: "Moves to Quoting when a quote is drafted.",
  },
  quoting: {
    label: EVENT_STAGE_LABELS.quoting,
    color: "hsl(var(--ev-info))",
    in: "A quote is drafted or sent. The customer hasn't accepted yet.",
    out: "Moves to Accepted when they accept. Lost if they decline, or the date passes first.",
  },
  accepted: {
    label: EVENT_STAGE_LABELS.accepted,
    color: "hsl(var(--accent))",
    in: "The customer accepted. No deposit recorded yet.",
    out: "Moves to Booked when the deposit comes in.",
    chg: 'Now holds unpaid events right up to the day. Inside 14 days the card turns red: "No deposit".',
  },
  booked: {
    label: EVENT_STAGE_LABELS.booked,
    color: "hsl(var(--ev-ok))",
    in: "Deposit paid. The event is more than 14 days away.",
    out: "Moves to Final details 14 days before the event.",
  },
  final_details: {
    label: EVENT_STAGE_LABELS.final_details,
    color: "hsl(var(--ev-later))",
    in: "Deposit paid and the event is 14 days away or less. Lock headcount, menu and timing. Collect the balance.",
    out: "Moves to Event day on the day.",
    chg: "Now needs a deposit. Before, every accepted event landed here 14 days out, paid or not.",
  },
  event_day: {
    label: EVENT_STAGE_LABELS.event_day,
    color: "hsl(var(--ev-hot))",
    in: "The event is today.",
    out: "Moves to Post-event the next day.",
  },
  post_event: {
    label: EVENT_STAGE_LABELS.post_event,
    color: "hsl(var(--ev-warn))",
    in: "The event happened. Thank the customer, ask for a review, collect any balance.",
    out: "Moves to Done once the balance is $0 and the thank-you and review ask are sent. Or mark it done yourself.",
    isNew: true,
  },
  done: {
    label: EVENT_STAGE_LABELS.done,
    color: "hsl(var(--ev-ok))",
    in: "Paid in full and followed up, or marked done.",
    out: CLOSED_OUT,
    chg: "Before, events jumped here the day after, even with money owing.",
  },
  lost: {
    label: EVENT_STAGE_LABELS.lost,
    color: "hsl(var(--muted-foreground))",
    in: "The customer declined, every quote was declined or lost, or the date passed without an acceptance.",
    out: CLOSED_OUT,
  },
  cancelled: {
    label: EVENT_STAGE_LABELS.cancelled,
    color: "hsl(var(--muted-foreground))",
    in: "Staff cancelled it. The only stage set by hand.",
    out: CLOSED_OUT,
  },
};

const DAY_MS = 86_400_000;

type RankQuote = Pick<StageQuote, "id" | "status" | "is_lost" | "pipeline_stage" | "created_at">;

/** Newest first, `id` breaking ties (the SQL's `created_at DESC, id DESC`). */
function newestFirst<T extends RankQuote>(quotes: T[]): T[] {
  return [...quotes].sort((a, b) =>
    a.created_at === b.created_at ? b.id.localeCompare(a.id) : a.created_at < b.created_at ? 1 : -1,
  );
}

/**
 * The quote a card shows: the committed live quote derive_event_stage ranks
 * first (accepted before a booking-stage quote, then newest), else the newest
 * live quote, else the newest quote.
 */
export function primaryQuote<T extends RankQuote>(quotes: T[]): T | null {
  const sorted = newestFirst(quotes);
  const committed = sorted.filter(
    (q) => isLiveQuote(q) && (q.status === "accepted" || ["deposit_pending", "booked", "won"].includes(q.pipeline_stage)),
  );
  const accepted = committed.find((q) => q.status === "accepted");
  return accepted ?? committed[0] ?? sorted.find(isLiveQuote) ?? sorted[0] ?? null;
}

const quoteBalance = (q: Pick<StageQuote, "total" | "amount_paid">) =>
  round2(Number(q.total) - Number(q.amount_paid));

/**
 * Money still owed: every live committed quote's total less what's paid, each
 * floored at 0 (the SQL's rule and the event page's Balance). With no
 * committed quote, nothing is owed.
 */
export function eventOwing(quotes: StageQuote[]): number {
  return round2(committedQuotes(quotes).reduce((s, q) => s + Math.max(0, quoteBalance(q)), 0));
}

/** Where Record payment lands: the oldest committed live quote with a balance, or null. */
export function owingQuote<T extends StageQuote>(quotes: T[]): T | null {
  return committedQuotes(quotes).find((q) => quoteBalance(q) > 0) ?? null;
}

export type QuoteStatusKey = "none" | "draft" | "sent" | "accepted" | "declined";
export const QUOTE_STATUS_KEYS: QuoteStatusKey[] = ["none", ...QUOTE_STATUSES];
export const QUOTE_STATUS_KEY_LABELS: Record<QuoteStatusKey, string> = {
  none: "No quote",
  draft: "Draft",
  sent: "Sent",
  accepted: "Accepted",
  declined: "Declined",
};

/** The Quote status filter's bucket for a quote: lost or declined is "declined". */
export function quoteStatusKey(q: Pick<StageQuote, "status" | "is_lost"> | null): QuoteStatusKey {
  if (!q) return "none";
  if (q.is_lost || q.status === "declined") return "declined";
  return (QUOTE_STATUSES as readonly string[]).includes(q.status) ? (q.status as QuoteStatusKey) : "draft";
}

const isServiceType = (s: string | null | undefined): s is ServiceType =>
  (SERVICE_TYPES as readonly string[]).includes(s ?? "");

type ServiceEvent = { event_type: EventType; fulfillment_type: string | null };

/** The service an event implies when its quotes don't say. */
function serviceFromEvent(event: ServiceEvent): ServiceType {
  if (event.event_type === "venue") return "event_space";
  if (event.event_type === "drop_off") return event.fulfillment_type === "pickup" ? "pickup" : "drop_off";
  return "full_service";
}

/** The quote service type a new draft starts with: the newest quote's, else the event's. */
export function serviceTypeForEvent(event: ServiceEvent, newestQuoteServiceType?: string | null): string {
  return newestQuoteServiceType || serviceFromEvent(event);
}

/** The board's service: the primary quote's, else the newest quote's, else the event's. */
export function boardServiceType(event: ServiceEvent & { quotes: PipelineQuote[] }): ServiceType {
  const primary = primaryQuote(event.quotes)?.service_type;
  if (isServiceType(primary)) return primary;
  const newest = newestFirst(event.quotes)[0]?.service_type;
  return isServiceType(newest) ? newest : serviceFromEvent(event);
}

/** Whose messages decide "No reply": the event's contact, else its primary quote's. */
export function eventContactId(event: { contact_id: string | null; quotes: PipelineQuote[] }): string | null {
  return event.contact_id ?? primaryQuote(event.quotes)?.contact_id ?? null;
}

/**
 * Whole days since our last message, when it's newer than the customer's
 * last one (or they never wrote). Null when they replied or we never wrote.
 */
export function noReplyDays(msg: ContactLastMessage | undefined, nowMs: number): number | null {
  const out = msg?.last_outbound_at ? Date.parse(msg.last_outbound_at) : NaN;
  if (Number.isNaN(out)) return null;
  const inbound = msg?.last_inbound_at ? Date.parse(msg.last_inbound_at) : NaN;
  if (!Number.isNaN(inbound) && inbound >= out) return null;
  return Math.max(0, Math.floor((nowMs - out) / DAY_MS));
}

/** "No reply · N days" shows from this many days. */
export const NO_REPLY_DAYS = 3;
/** Inside this many days an unpaid Accepted card turns red. */
const NO_DEPOSIT_DAYS = 14;

/** Days from `today` (local midnight) to the date: negative in the past. */
function daysFrom(iso: string, today: Date): number {
  return Math.round((parseISO(iso).getTime() - today.getTime()) / DAY_MS);
}

export function rel(n: number): string {
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n === -1) return "yesterday";
  return n > 0 ? `in ${n} days` : `${-n} days ago`;
}

/** "Fri, Oct 30", plus the year when it isn't this year. */
export function fmtDate(iso: string, today: Date): string {
  const d = parseISO(iso);
  return d.toLocaleDateString("en-CA", {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== today.getFullYear() ? { year: "numeric" as const } : {}),
  });
}

/** "$3,231.80" or "$980": cents only when there are some. */
export function fmtMoney(n: number): string {
  return `$${n.toLocaleString("en-CA", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
}

export type FlagTone = "warn" | "hot" | "mute" | "ok";
export interface EventFlag {
  tone: FlagTone;
  text: string;
}

export interface FlagInput {
  stage: EventStage;
  daysOut: number | null;
  owing: number;
  noReplyDays: number | null;
  isPartial: boolean;
}

/** The pills on a card or List row; `alert` (a red left border) when one is hot. */
export function eventFlags({ stage, daysOut, owing, noReplyDays: quiet, isPartial }: FlagInput): {
  flags: EventFlag[];
  alert: boolean;
} {
  const flags: EventFlag[] = [];
  const open = !CLOSED_STAGES.includes(stage);
  if (isPartial) flags.push({ tone: "warn", text: "Incomplete" });
  if (daysOut === null && open) flags.push({ tone: "warn", text: "No date yet" });
  if (stage === "accepted" && daysOut !== null && daysOut <= NO_DEPOSIT_DAYS) {
    flags.push({ tone: "hot", text: `No deposit · event ${rel(daysOut)}` });
  } else if (stage === "accepted") {
    flags.push({ tone: "mute", text: "Waiting on deposit" });
  }
  if (stage === "final_details" || stage === "event_day") {
    flags.push(owing > 0 ? { tone: "warn", text: `Balance ${fmtMoney(owing)} due` } : { tone: "ok", text: "Paid in full" });
  }
  if ((stage === "lead" || stage === "quoting") && quiet !== null && quiet >= NO_REPLY_DAYS) {
    flags.push({ tone: "mute", text: `No reply · ${quiet} days` });
  }
  return { flags, alert: flags.some((f) => f.tone === "hot") };
}

/** Everything a card, a List row, the filters and the summary need about one event. */
export interface BoardRow {
  id: string;
  event: PipelineEvent;
  name: string;
  occasion: string | null;
  stage: EventStage;
  date: string | null;
  /** Days from today; null when there's no date. */
  daysOut: number | null;
  /** "Fri, Oct 30 · in 3 days", or "Date not set". */
  dateText: string;
  guests: number;
  service: ServiceType;
  serviceLabel: string;
  quote: PipelineQuote | null;
  quoteVersion: number | null;
  quoteStatus: QuoteStatusKey;
  quoteStatusLabel: string;
  /** Quotes on the event besides the primary one ("+N"). */
  otherQuotes: number;
  /** The primary quote's total; 0 without a quote. */
  total: number;
  owing: number;
  /** Where Record payment lands; null when nothing is owed. */
  owingQuote: PipelineQuote | null;
  noReplyDays: number | null;
  flags: EventFlag[];
  alert: boolean;
  thanked: boolean;
  reviewAsked: boolean;
  isPartial: boolean;
}

export function toBoardRow(
  event: PipelineEvent,
  today: Date,
  nowMs: number,
  lastMessage: ContactLastMessage | undefined,
): BoardRow {
  const stage = normalizeStage(event.stage);
  const quote = primaryQuote(event.quotes);
  const daysOut = event.event_date ? daysFrom(event.event_date, today) : null;
  const owing = eventOwing(event.quotes);
  const quiet = noReplyDays(lastMessage, nowMs);
  const service = boardServiceType(event);
  const status = quoteStatusKey(quote);
  return {
    id: event.id,
    event,
    name: event.client_name?.trim() || "(no name)",
    occasion: event.occasion?.trim() || null,
    stage,
    date: event.event_date,
    daysOut,
    dateText: event.event_date && daysOut !== null ? `${fmtDate(event.event_date, today)} · ${rel(daysOut)}` : "Date not set",
    guests: event.guest_count ?? 0,
    service,
    serviceLabel: SERVICE_TYPE_SHORT_LABELS[service],
    quote,
    quoteVersion: quote ? (quoteVersions(event.quotes).get(quote.id) ?? 1) : null,
    quoteStatus: status,
    quoteStatusLabel: QUOTE_STATUS_KEY_LABELS[status],
    otherQuotes: Math.max(0, event.quotes.length - 1),
    total: quote ? Number(quote.total) : 0,
    owing,
    owingQuote: owingQuote(event.quotes),
    noReplyDays: quiet,
    ...eventFlags({ stage, daysOut, owing, noReplyDays: quiet, isPartial: event.is_partial }),
    thanked: !!event.thank_you_sent_at,
    reviewAsked: !!event.review_requested_at,
    isPartial: event.is_partial,
  };
}

/** The loaded events as board rows. No-reply is left out when messages didn't load. */
export function toBoardRows(data: EventsPipelineData | undefined, today: Date, nowMs: number): BoardRow[] {
  if (!data) return [];
  return data.events.map((e) => {
    const contact = data.messagesUnavailable ? null : eventContactId(e);
    return toBoardRow(e, today, nowMs, contact ? data.lastMessages[contact] : undefined);
  });
}
